import React from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { ChannelControl, ChannelControlStyle } from './ChannelControl';
import { DefaultTag } from './DefaultTag';
import { BORDER, INK, RADIUS, SURFACE } from './notificationTokens';
import {
  NotificationCategoryDefinitionType,
  NotificationChannelDefinitionType,
  NotificationRoleDefinitionType,
  NotificationTypeChannels,
  effectiveOn,
  isOwnerLocked,
} from '../../util/NotificationUtil';

interface NotificationCardsProps {
  roles: NotificationRoleDefinitionType[];
  channels: NotificationChannelDefinitionType[];
  categories: NotificationCategoryDefinitionType[];
  settings: NotificationTypeChannels;
  controlStyle: ChannelControlStyle;
  onToggle: (channel: string, category: string, role: string, value: boolean) => void;
}

const NotificationCards = ({
  roles,
  channels,
  categories,
  settings,
  controlStyle,
  onToggle,
}: NotificationCardsProps) => {
  const ownerValue = roles[0]?.value;

  return (
    <Stack spacing={2.5}>
      {categories.map((category) => (
        <Card
          key={category.value}
          variant="outlined"
          sx={{ borderColor: BORDER.subtle, borderRadius: RADIUS.panel, overflow: 'hidden' }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              backgroundColor: SURFACE.cool,
              borderBottom: `1px solid ${BORDER.subtle}`,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: INK.heading }}>{category.label}</Typography>
            <Typography sx={{ fontSize: 13, color: INK.secondary, mt: 0.5 }}>{category.tooltip}</Typography>
          </Box>

          <Box sx={{ px: 2.5 }}>
            {roles.map((role, roleIndex) => {
              const isLastRole = roleIndex === roles.length - 1;
              return (
                <Box
                  key={role.value}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    py: 2,
                    borderBottom: isLastRole ? 'none' : `1px solid ${BORDER.subtle}`,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <Typography component="span" sx={{ fontSize: 16, fontWeight: 600, color: INK.heading }}>
                        {role.label}
                      </Typography>
                      {role.value === ownerValue && <DefaultTag />}
                    </Box>
                    {role.sub && (
                      <Typography sx={{ fontSize: 12, color: INK.secondary }}>{role.sub}</Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                    {channels.map((channel) => {
                      const row = settings[channel.value]?.[category.value];
                      const locked = role.value === ownerValue && isOwnerLocked(roles, row);
                      const on = effectiveOn(roles, row, role.value);
                      const stored = !!row?.[role.value];
                      return (
                        <ChannelControl
                          key={channel.value}
                          channelValue={channel.value}
                          channelLabel={channel.label}
                          on={on}
                          locked={locked}
                          controlStyle={controlStyle}
                          showLabel
                          onToggle={() => onToggle(channel.value, category.value, role.value, !stored)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      ))}
    </Stack>
  );
};

export { NotificationCards };
